import dynamic from "next/dynamic";
import React, { useMemo, useState } from "react";

import { Container } from "@/features/common/components/layout/Container";
import { PostTypeFilter } from "@/features/post/components/PostTypeFilter";
import { About } from "@/features/user/components";
import ProfileMasonryPlaceholder from "@/features/user/components/ProfileMasonryPlaceholder";
import { NFTAbout } from "@/features/user/components/about/NFTAbout";
import {
  PostTypeEnum,
  useProfileChannelPostCountQuery,
} from "@/generated/generated-hooks";
import { isCollectiblesOnly } from "@/helpers/post/collectiblesPostType";
import { QueryPostPageArgs } from "@/server/graphql/generated/generated-types";

const InfiniteMasonry = dynamic(
  () =>
    import("@/features/post/components/InfiniteMasonry").then(
      (mod) => mod.InfiniteMasonry,
    ),
  { ssr: false, loading: () => <ProfileMasonryPlaceholder /> },
);

type ProfileChannelMasonrySectionProps = {
  channelDescription?: string;
  followerCount: number;
  walletAccount: string;
  channelId: string;
  slug: string;
  isOwner: boolean;
};

const ProfileChannelMasonrySection = ({
  followerCount,
  walletAccount,
  channelId,
  slug,
  isOwner,
  channelDescription,
}: ProfileChannelMasonrySectionProps) => {
  const [selectedPostTypes, setSelectedPostTypes] = useState<PostTypeEnum[]>(
    [],
  );

  const { data, loading, error } = useProfileChannelPostCountQuery({
    variables: {
      channelSlug: slug as string,
      postTypes: selectedPostTypes,
    },
  });

  const isCollectibleSelected = isCollectiblesOnly(selectedPostTypes);

  const onClickPostFilter = (types: PostTypeEnum[]) =>
    setSelectedPostTypes(types);

  const postVariables: Omit<QueryPostPageArgs, "paging"> = useMemo(() => {
    if (!channelId) return;
    const variables: Omit<QueryPostPageArgs, "paging"> = {
      input: {
        latest: true,
        channelId,
        isOwner,
        showSensitiveContent: true,
        showPaidContent: true,
      },
    };
    if (selectedPostTypes?.length > 0)
      variables.input.postTypes = selectedPostTypes;
    return variables;
  }, [selectedPostTypes, channelId, isOwner]);

  if (!data || loading) {
    return (
      <>
        <Container>
          <PostTypeFilter
            currentTypes={selectedPostTypes}
            onClick={onClickPostFilter}
          />
        </Container>
        <ProfileMasonryPlaceholder />
      </>
    );
  }

  const { currentMonth, lastMonth, total } = data.channel.blurPostCount ?? {};
  const { postCount, subscriberCount, currentMonthPostCount } = data.channel;

  return (
    <>
      {/* @todo move to upper level */}
      <Container>
        <PostTypeFilter
          currentTypes={selectedPostTypes}
          onClick={onClickPostFilter}
        />
      </Container>

      <div id="post-section" />

      <InfiniteMasonry
        isProfileOwner={isOwner}
        postPageVariables={postVariables}
        about={
          isCollectibleSelected ? (
            <NFTAbout
              walletAccount={walletAccount}
              currentMonthPostCount={currentMonthPostCount}
              allNftPostCount={postCount}
            />
          ) : (
            <About
              channelDescription={channelDescription}
              walletAccount={walletAccount}
              isOwner={isOwner}
              aboutInformation={{
                numberOfPosts: postCount ?? 0,
                followers: followerCount,
                monthlyPosts: currentMonthPostCount,
                subscriberCount,
              }}
              blurPostCount={{ total, lastMonth, currentMonth }}
            />
          )
        }
      />
    </>
  );
};

export default ProfileChannelMasonrySection;
